import { useState } from "react";
import { useAssetCategories } from "@/hooks/useAssets";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LoadingSpinnerWithText } from "@/components/ui/loading-spinner";
import { Input } from "@/components/ui/input";
import { Plus, Search, Edit, Trash2, Package, Monitor, Key } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { AssetCategoryDialog } from "@/components/assets/AssetCategoryDialog";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

export default function AssetCategories() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  
  const { data: categories = [], isLoading, refetch } = useAssetCategories();
  
  // Filter categories
  const filteredCategories = categories.filter(category => {
    const matchesSearch = searchQuery === "" || 
      category.name_ar?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      category.name_en?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      category.category_type?.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesSearch;
  });
  
  const handleEditCategory = (category: any) => {
    setSelectedCategory(category);
    setIsDialogOpen(true);
  };
  
  const handleAddCategory = () => {
    setSelectedCategory(null);
    setIsDialogOpen(true);
  };
  
  const handleDeleteCategory = async (id: string) => {
    if (!confirm("هل أنت متأكد من حذف هذا التصنيف؟")) return;
    
    const { error } = await supabase
      .from("asset_categories")
      .delete()
      .eq("id", id);
    
    if (error) {
      toast.error("حدث خطأ أثناء حذف التصنيف");
      console.error(error);
    } else {
      toast.success("تم حذف التصنيف بنجاح");
      refetch();
    }
  };
  
  const getAssetTypeIcon = (type: string) => {
    switch (type) {
      case 'physical':
        return <Package className="w-4 h-4" />;
      case 'digital':
        return <Monitor className="w-4 h-4" />;
      case 'credentials':
        return <Key className="w-4 h-4" />;
      default:
        return <Package className="w-4 h-4" />;
    }
  };
  
  const getAssetTypeLabel = (type: string) => {
    switch (type) {
      case 'physical':
        return 'مادية';
      case 'digital':
        return 'رقمية';
      case 'credentials':
        return 'بيانات دخول';
      default:
        return type;
    }
  };
  
  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <LoadingSpinnerWithText text="جاري تحميل التصنيفات..." />
      </div>
    );
  }
  
  return (
    <div className="container mx-auto p-6 space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-card-foreground">تصنيفات العهد</h1>
          <p className="text-muted-foreground mt-1">إدارة تصنيفات العهد وأنواعها</p>
        </div>
        <Button onClick={handleAddCategory} className="gap-2">
          <Plus className="w-4 h-4" />
          إضافة تصنيف جديد
        </Button>
      </div>
      
      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              placeholder="البحث في التصنيفات..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pr-10"
            />
          </div>
        </CardContent>
      </Card>
      
      {/* Categories Grid */}
      {filteredCategories.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">لا توجد تصنيفات</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCategories.map((category) => (
            <Card key={category.id} className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-2">
                    <div className={`p-2 rounded-lg ${category.color || 'bg-primary/10'}`}>
                      {getAssetTypeIcon(category.asset_type)}
                    </div>
                    <div>
                      <CardTitle className="text-lg">{category.name_ar}</CardTitle>
                      {category.name_en && (
                        <p className="text-sm text-muted-foreground">{category.name_en}</p>
                      )}
                    </div>
                  </div>
                  <Badge variant="secondary">
                    {getAssetTypeLabel(category.asset_type)}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">نوع التصنيف</p>
                  <p className="text-sm">{category.category_type}</p>
                </div>
                
                {category.description && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">الوصف</p>
                    <p className="text-sm">{category.description}</p>
                  </div>
                )}
                
                <div className="flex gap-2 flex-wrap">
                  {category.requires_serial && (
                    <Badge variant="outline" className="text-xs">
                      يتطلب رقم تسلسلي
                    </Badge>
                  )}
                  {category.requires_return && (
                    <Badge variant="outline" className="text-xs">
                      يتطلب إرجاع
                    </Badge>
                  )}
                </div>
                
                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEditCategory(category)}
                    className="flex-1"
                  >
                    <Edit className="w-4 h-4 ml-2" />
                    تعديل
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDeleteCategory(category.id)}
                    className="flex-1"
                  >
                    <Trash2 className="w-4 h-4 ml-2" />
                    حذف
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      
      {/* Category Dialog */}
      <AssetCategoryDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        category={selectedCategory}
        onSuccess={() => refetch()}
      />
    </div>
  );
}